(ns practitest.frontend.tests.test-form.copy-steps
  (:require [ajax.core :as ajax]
            [clojure.set]
            [practitest.frontend.common-entities.events :as common-events]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.i18n :refer [format-translation get-translation]]
            [practitest.frontend.common.re-frame :refer [partial-dispatch partial-subscribe]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.modal :as modal]
            [practitest.frontend.components2.add-entities :as add-entities]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.grid :as grid]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.tests.test-form.common :refer [copy-steps-form-id
                                                                get-last-position
                                                                random-step-id
                                                                insert-steps-at
                                                                steps-form-id
                                                                init-step-form-object]]
            [practitest.frontend.tests.test-form.tests-grid :as tests-grid]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]))

(def grid-entity-type :test)

;; State overview:

(comment
  ;; Root form state path - kind of sub-form
  [:form-states2 steps-form-id copy-steps-form-id]

  {
   ;; Whether to show modal dialog
   :show? false
   ;; Set of IDs of tests that are currently expanded in grid
   :expanded-tests #{}
   ;; Set of IDs of steps that are currently selected in grid
   :selected-steps #{}
   ;; Map of <test-id> -> [steps collection] - will contain steps data for all currently expanded tests
   :steps-data {}})



;;; Subscriptions

(re-frame/reg-sub
  ::form-state
  (fn [db]
    (get-in db [:form-states2 steps-form-id copy-steps-form-id])))

(re-frame/reg-sub
  ::show-copy-steps?
  :<- [::form-state]
  (fn [form-state]
    (:show? form-state)))

(re-frame/reg-sub
  ::selection-disabled?
  (fn [[_ row]]
    (re-frame/subscribe [::tests-grid/expandable-row? row]))
  (fn [expandable?]
    (not expandable?)))

(re-frame/reg-sub
  ::test-selected?
  :<- [::form-state]
  (fn [form-state [_ test-id]]
    (let [step-ids (map :id (get-in form-state [:steps-data test-id]))]
      ;; Test is selected if all steps are selected
      (and (seq step-ids)
           (clojure.set/subset? (set step-ids)
                                (:selected-steps form-state))))))

(re-frame/reg-sub
  ::selected-step-ids
  :<- [::form-state]
  (fn [form-state]
    (:selected-steps form-state)))

(re-frame/reg-sub
  ::step-selected?
  :<- [::form-state]
  (fn [form-state [_ step]]
    (contains? (:selected-steps form-state) (:id step))))

(re-frame/reg-sub
  ::add-position
  :<- [::form-state]
  (fn [form-state]
    (:add-position form-state)))

;;; Events

(re-frame/reg-event-db
  ::reset-selection
  (fn [db]
    (update-in db
               [:form-states2 steps-form-id copy-steps-form-id]
               dissoc :steps-data :selected-steps :expanded-tests)))

(re-frame/reg-event-db
  ::on-expand-data-fetched
  (fn [db [_ test-id selected? response]]
    (-> db
        (update :progress disj [::tests-grid/fetch-step-data copy-steps-form-id test-id])
        (assoc-in [:form-states2 steps-form-id copy-steps-form-id :steps-data test-id]
                  ;; Save test-id inside step data
                  (map #(assoc % :test_id (:id (:entity response))) (:steps response)))

        ;; Assoc selection if triggered from test selection
        (cond-> selected?
          (update-in [:form-states2 steps-form-id copy-steps-form-id :selected-steps]
                     (fn [selected-steps]
                       (apply (fnil conj #{}) selected-steps (map :id (:steps response)))))))))

(re-frame/reg-event-fx
  ::expand-row-and-fetch-data
  (fn [{db :db} [_ test-id selected?]]
    {:db (-> db
             (update-in [:form-states2 steps-form-id copy-steps-form-id :expanded-tests] (fnil conj #{}) test-id)
             (update :progress conj [::tests-grid/fetch-step-data copy-steps-form-id test-id]))
     :http-xhrio
     {:method :get
      :uri (broutes/get-url "qtest_designs#entity"
                            {:p_id (subs/get-current-project-id db)
                             :id test-id})
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success [::on-expand-data-fetched test-id selected?]
      :on-failure [::tests-grid/on-expand-data-error copy-steps-form-id test-id]}}))

(re-frame/reg-event-db
  ::collapse-row
  (fn [db [_ test-id]]
    (-> db
        (update-in [:form-states2 steps-form-id copy-steps-form-id]
                   (fn [form-state]
                     (let [step-ids (map :id (get-in form-state [:steps-data test-id]))]
                       (-> form-state
                           (update :expanded-tests disj test-id)
                           (update :steps-data dissoc test-id)
                           ;; Remove selection for collapsed steps
                           (update :selected-steps #(apply disj % step-ids)))))))))

(re-frame/reg-event-fx
  ::toggle-expand-row
  (fn [_ [_ test-row]]
    (let [test-id (:id test-row)
          expanded? @(re-frame/subscribe [::tests-grid/expanded-row? copy-steps-form-id test-row])]
      (if expanded?
        {:dispatch [::collapse-row test-id]}
        {:dispatch [::expand-row-and-fetch-data test-id false]}))))

(re-frame/reg-event-fx
  ::toggle-test-selection
  (fn [{db :db} [_ test-id clear?]]
    ;; Special case for filters apply case
    (if clear?
      {:dispatch [::reset-selection]}
      (let [selected? @(re-frame/subscribe [::test-selected? test-id])
            expanded? @(re-frame/subscribe [::tests-grid/expanded-row? copy-steps-form-id {:id test-id}])]
        (cond
         ;; Just deselect if already selected
          selected?
          {:db (update-in db [:form-states2 steps-form-id copy-steps-form-id]
                          (fn [form-state]
                            (let [step-ids (map :id (get-in form-state [:steps-data test-id]))]
                              (-> form-state
                                  (update :selected-steps #(apply disj % step-ids))))))}
         ;; Already expanded - just select, no need to re-fetch
          expanded?
          {:db (update-in db [:form-states2 steps-form-id copy-steps-form-id]
                          (fn [form-state]
                            (let [step-ids (map :id (get-in form-state [:steps-data test-id]))]
                              (-> form-state
                                  (update :selected-steps #(apply (fnil conj #{}) % step-ids))))))}
         ;; Fetch steps if test is selected - we need steps data later to add them
          :else
          {:dispatch [::expand-row-and-fetch-data test-id true]})))))

(re-frame/reg-event-db
  ::toggle-step-selection
  (fn [db [_ step]]
    (let [step-selected? @(re-frame/subscribe [::step-selected? step])]
      (update-in db
                 [:form-states2 steps-form-id copy-steps-form-id :selected-steps]
                 (if step-selected?
                   disj
                   (fnil conj #{}))
                 (:id step)))))

(re-frame/reg-event-fx
  ::add-selected-steps
  (fn [{db :db} [_ add-position]]
    (let [all-steps-map (into {}
                              (map (fn [step]
                                     [(:id step) step]))
                              (mapcat val (get-in db [:form-states2 steps-form-id copy-steps-form-id :steps-data])))
          selected-step-ids (get-in db [:form-states2 steps-form-id copy-steps-form-id :selected-steps])
          selected-steps (vals (select-keys all-steps-map selected-step-ids))
          ;; Ordering by position inside a test, ordering of tests is undefined for now
          selected-steps (sort-by (juxt :test_id :position) selected-steps)]
      {:db (-> db
               (update-in [:form-states2 steps-form-id :steps]
                          (fn [steps]
                            (let [add-position (or add-position
                                                   (get-last-position steps))
                                   new-steps (map (fn [selected-step]
                                                    ;; Position will be recalculated in insert-steps-at
                                                    (-> (init-step-form-object (random-step-id) 0 true selected-step) 
                                                        (assoc-in [:fields :copied_from_step] (:id selected-step))
                                                        (assoc-in [:fields :copied_from_test] (:test_id selected-step))))
                                                  selected-steps)]
                              (insert-steps-at steps new-steps add-position))))
               (assoc-in [:form-states2 steps-form-id :dirty?] true))
       :dispatch [::hide-copy-steps]})))

(re-frame/reg-event-db
  ::show-copy-steps
  (fn [db [_ add-position]]
    (update-in db [:form-states2 steps-form-id copy-steps-form-id]
               assoc :show? true :add-position add-position)))

(re-frame/reg-event-db
  ::hide-copy-steps
  (fn [db]
    (update-in db [:form-states2 steps-form-id] dissoc copy-steps-form-id)))

(re-frame/reg-event-db
  ::toggle-copy-step
  (fn [db]
    (update-in db [:form-states2 steps-form-id copy-steps-form-id :show?] not)))

;;; Components

(defn- expandable-row-test-steps [grid-props idx row]
  (when @(re-frame/subscribe [::tests-grid/expanded-row? copy-steps-form-id row])
    (let [test-id (:id row)
          even-row? (even? idx)
          shown-columns (:shown-columns grid-props)
          columns-count (count shown-columns)
          step-data @(re-frame/subscribe [::tests-grid/steps-data copy-steps-form-id test-id])
          progress-key [::tests-grid/fetch-step-data copy-steps-form-id test-id]
          loading? @(re-frame/subscribe [::subs/progress? progress-key])]
      (into
        [:<>
         (when loading?
           [:tr {:class (dom/classes-pairs-v :grid-row-even even-row?)}
            [:td {:col-span columns-count}
             [spinners/mux-spinner {:size :small
                                    :delay 100}]]])]

        (map-indexed
          (fn [idx step]
            [:tr {:class (dom/classes-pairs-v :grid-row-even even-row?)
                  :key (:id step)}
             ;; First column is always selection column
             [:td.hierarchical-child-cell
              [:span.hierarchical-child-icon
               [icons/hierarchical-child]]
              [grid/row-selector
               {:selected? (re-frame/subscribe [::step-selected? step])
                :disabled? false
                :on-change (fn [e]
                             (re-frame/dispatch [::toggle-step-selection step]))}]]
             ;; Step label and name - span remaining columns
             [:td {:col-span (max 1 columns-count)}
              [:span (str "Step " (inc idx) ": ")]
              [:span (:name step)]]]))
        step-data))))

(defn copy-step-modal [form-props]
  (let [grid-state (reagent/atom {})
        show? (re-frame/subscribe [::show-copy-steps?])
        hide! #(re-frame/dispatch [::hide-copy-steps])]
    (fn [form-props]
      [modal/bootstrap-modal
       {:is-open? @show?
        :on-request-close hide!
        :container-ref (:main-ref form-props)
        :class "copy-steps-modal"
        :close-button? true}
       ;; header
       [:span (get-translation "test.steps.copy-steps.modal-header" "Copy Steps to Test")]
       ;; body
       [add-entities/add-entities
        (merge
          (tests-grid/default-grid-props copy-steps-form-id [::reset-selection] grid-state)
          {:on-filter-changed (partial-dispatch [::reset-selection])

           ;; Selection customization
           :all-selected? (constantly (atom false))
           :disabled-select-all? (constantly (atom true))
           :disabled-select-all-tooltip (get-translation "test.steps.copy-steps.select-all-tooltip"
                                                         "You need to select individual steps in this grid")
           :disabled-row? (fn [row]
                            @(re-frame/subscribe [::selection-disabled? row]))
           :disabled-row-tooltip (fn [_row]
                                   (get-translation "test.steps.copy-steps.no-steps-test-tooltip"
                                                    "This test doesn't have any steps to copy"))
           :toggle-selection (partial-dispatch [::toggle-test-selection])
           :selected? (partial-subscribe [::test-selected?])

           ;; Expandable row glue code
           :expandable-row? (partial-subscribe [::tests-grid/expandable-row?])
           :expanded-row? (partial-subscribe [::tests-grid/expanded-row? copy-steps-form-id])
           :expandable-row-component expandable-row-test-steps
           :on-expand-row-click (fn [e row]
                                  (re-frame/dispatch [::toggle-expand-row row]))})]
       ;; footer
       [:div.m-button-group
        [buttons/button
         {:text (get-translation "generic.button.cancel" "Cancel")
          :on-click hide!}]
        (let [step-ids @(re-frame/subscribe [::selected-step-ids])
              count (if (seq step-ids)
                      (count step-ids)
                      0)]
          [buttons/button
           {:primary? true
            :disabled? (zero? count)
            :text (format-translation (get-translation "test.steps.copy-steps.add-selected-steps-button-with-count"
                                                       "Add selected Steps (%{count})")
                                      :count count)
            :icon [icons/mux-plus-circle]
            :on-click (fn [e]
                        (re-frame/dispatch [::add-selected-steps @(re-frame/subscribe [::add-position])]))}])]])))
